import { Invoice } from '@wonderschool/common-base-types';
import { Button, Dialog, Input, Label } from '@wonderschool/common-base-ui';
// eslint-disable-next-line no-restricted-imports
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { dateFormatter, DefaultDateFormat } from '../../../../helpers/dates';
import { hasParentPaidInvoice, InvoiceStatus } from '../../../../helpers/invoices';
import { USER_STATUS } from '../../../../helpers/userStatus';
import AlertMessage, { Alert } from '../../../Shared/AppAlert';
import { DatePicker } from '../../../Shared/DatePicker';

// Allow editing a ONE-TIME invoice (no invoicePlanId) that has NOT been paid or
// is not mid-charge. Recurring-plan invoices are managed via the plan; paid /
// processing / void invoices are locked so we never change the amount owed after
// money has moved (accounting/reconciliation safety). Editing is limited to the
// due date and notes here — changing the amount requires void + recreate so
// processing fees are recomputed correctly (tracked as a follow-up).
export function invoiceCanEdit(invoice): boolean {
  const hasInvoiceBeenPaid = hasParentPaidInvoice(invoice?.payments);
  return (
    !invoice?.invoicePlanId &&
    !hasInvoiceBeenPaid &&
    !invoice?.alreadyPaid &&
    !invoice?.manuallyPaid &&
    invoice?.userStatus !== USER_STATUS.PAID &&
    invoice?.userStatus !== USER_STATUS.PROCESSING &&
    invoice?.userStatus !== USER_STATUS.TRANSFERRING &&
    ![InvoiceStatus.PROCESSING, InvoiceStatus.VOID, InvoiceStatus.PAID].includes(invoice?.status)
  );
}

type InvoiceEditDialogProps = {
  invoice: Invoice;
  isOpen: boolean;
  onClose: () => void;
  onInvoiceUpdate: (data: Partial<Invoice>) => Promise<void> | void;
  onSuccess?: (updatedData: Partial<Invoice>) => void;
};

export function InvoiceEditDialog({ invoice, isOpen, onClose, onInvoiceUpdate, onSuccess }: InvoiceEditDialogProps) {
  const { t } = useTranslation();
  const [editDueDate, setEditDueDate] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [editInvoiceAlert, setEditInvoiceAlert] = useState<Alert | null>(null);

  // Seed the form from the invoice each time the dialog opens.
  useEffect(() => {
    if (!isOpen) return;
    setEditInvoiceAlert(null);
    setEditDueDate((invoice as any)?.dateDue ? (dateFormatter((invoice as any).dateDue) ?? '') : '');
    setEditNotes((invoice as any)?.notes ?? '');
  }, [isOpen, invoice]);

  const onSaveEdit = async () => {
    const update = {
      dateDue: editDueDate ? moment(editDueDate, DefaultDateFormat).valueOf() : (invoice as any).dateDue,
      notes: editNotes,
    };

    setIsSavingEdit(true);
    setEditInvoiceAlert(null);

    try {
      await onInvoiceUpdate(update);
      onSuccess?.(update);
      onClose();
    } catch (error) {
      console.error(error);
      setEditInvoiceAlert({
        type: 'error',
        message: 'Something went wrong, please try again',
      });
    } finally {
      setIsSavingEdit(false);
    }
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose}>
      <Dialog.Panel>
        <Dialog.Title>{t('billing.invoice.editInvoice')}</Dialog.Title>
        <div className="flex flex-col p-4 pt-4">
          <Label extraClasses="pb-1">{t('billing.invoice.editDueDateLabel')}</Label>
          <DatePicker
            name="editDueDate"
            // DatePicker rejects dates not strictly after minDate; use end of
            // yesterday so today is a valid (earliest) due date.
            minDate={moment().subtract(1, 'day').endOf('day')}
            value={editDueDate}
            onChange={(_e, { value }) => setEditDueDate(value)}
          />
          <Label extraClasses="pt-4 pb-1">{t('billing.invoice.editNotesLabel')}</Label>
          <Input
            inputType="text"
            placeholder={t('billing.invoice.editNotesPlaceholder')}
            value={editNotes}
            onChange={(e) => setEditNotes(e.target.value)}
          />
          <AlertMessage alert={editInvoiceAlert} setAlert={setEditInvoiceAlert} />
          <span className="mt-6">
            <Button primary extraClasses="w-40" loading={isSavingEdit} disabled={isSavingEdit} onClick={onSaveEdit}>
              {t('common.save')}
            </Button>
            <Button extraClasses="ml-4" data-testid="cancel-edit-btn" disabled={isSavingEdit} onClick={onClose}>
              {t('common.cancel')}
            </Button>
          </span>
        </div>
      </Dialog.Panel>
    </Dialog>
  );
}
